import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getActiveProviderAlerts,
  getFulfilledProviderAlerts,
  getProviderAlertHistory,
} from "../APIs/providerAlerts";
import { getProvider } from "../APIs/providerApi";
import { useCurrentUser } from "../APIs/UserContext";
import { Navigation } from "lucide-react";

const TABS = [
  { key: "triggered", label: "Triggered Alerts" },
  { key: "active", label: "Active Alerts" },
  { key: "history", label: "Alert History" },
];

export default function AlertsTab() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("triggered");
  const { currentUser } = useCurrentUser();
  // Derive patientId from currentUser
  const patientId = currentUser?.patientId || null;

  const [data, setData] = useState({
    triggered: [],
    active: [],
    history: [],
  });

  const [loading, setLoading] = useState({
    triggered: false,
    active: false,
    history: false,
  });

  const [error, setError] = useState({
    triggered: null,
    active: null,
    history: null,
  });

  const [initialized, setInitialized] = useState({
    triggered: false,
    active: false,
    history: false,
  });

  // Build a Google Maps directions link for a facility
  const mapsLink = (facility) => {
    const address = [facility.addressLine, facility.city, facility.state]
      .filter(Boolean)
      .join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  // Fetch for a specific tab
  const fetchTab = async (tabKey) => {
    setLoading((prev) => ({ ...prev, [tabKey]: true }));
    setError((prev) => ({ ...prev, [tabKey]: null }));

    try {
      let res = [];
      if (tabKey === "triggered") {
        // /api/provider-alerts/fulfilled
        const raw = await getFulfilledProviderAlerts();
        const mapped = (raw || []).map((item, idx) => ({
          id: item.alertId || idx,
          alertId: item.alertId || idx,
          providerId: item.providerId,
          providerName:
            [item.firstName, item.lastName].filter(Boolean).join(" ") ||
            "Provider",
          providerEmail: item.email || "",
          specialty: item.specialtyLabel || "N/A",
          city: item.city || "",
          state: item.state || "",
          phoneNumber: item.phoneNumber || "",
          fulfilledAt: item.fulfilledAt || null,
        }));

        // Deduplicate by providerId — one card per provider
        const grouped = {};
        for (const item of mapped) {
          const key = item.providerId || item.id;
          if (!grouped[key]) {
            grouped[key] = {
              ...item,
              id: item.providerId || item.id,
              alertIds: [item.alertId],
              locations: [{ city: item.city, state: item.state }],
            };
          } else {
            grouped[key].alertIds.push(item.alertId);
            grouped[key].locations.push({ city: item.city, state: item.state });
            // Keep latest fulfilledAt
            if (item.fulfilledAt > grouped[key].fulfilledAt) {
              grouped[key].fulfilledAt = item.fulfilledAt;
            }
          }
        }

        const deduped = Object.values(grouped);

        // Fetch full facility info for each unique provider
        await Promise.all(
          deduped.map(async (entry) => {
            if (!entry.providerId) return;
            try {
              const provider = await getProvider(entry.providerId);
              const src = Array.isArray(provider?.facilityInformation)
                ? provider.facilityInformation
                : Array.isArray(provider?.facilityLocations)
                  ? provider.facilityLocations
                  : [];
              entry.facilities = src
                .map((f) => ({
                  name: f?.facilityName,
                  addressLine: f?.addressLine,
                  city: f?.city,
                  state: f?.state,
                }))
                .filter((x) => x.city && x.state);
            } catch {
              // Fall back to basic city/state from alert data
              entry.facilities = [];
            }
          }),
        );

        res = deduped;
      } else if (tabKey === "active") {
        // /api/provider-alerts/created
        const raw = await getActiveProviderAlerts();
        res = (raw || []).map((a) => ({
          id: a.providerAlertsId,
          specialty: a.specialty,
          city: a.city,
          state: a.state,
          startDateTime: a.startDateTime,
          endDateTime: a.endDateTime,
          createdAt: a.createdAt,
          isActive: a.isActive,
        }));
      } else if (tabKey === "history") {
        // /api/provider-alerts/history
        const raw = await getProviderAlertHistory();
        res = (raw || []).map((a) => ({
          id: a.providerAlertsId,
          specialty: a.specialty,
          city: a.city,
          state: a.state,
          startDateTime: a.startDateTime,
          endDateTime: a.endDateTime,
          createdAt: a.createdAt,
          isActive: a.isActive,
        }));
      }

      setData((prev) => ({ ...prev, [tabKey]: res }));
      setInitialized((prev) => ({ ...prev, [tabKey]: true }));
    } catch (err) {
      const msg =
        err?.response?.status === 401
          ? "You must be signed in as a patient to view alerts."
          : err?.response?.data?.message ||
            err?.message ||
            "Failed to load alerts.";
      setError((prev) => ({ ...prev, [tabKey]: msg }));
    } finally {
      setLoading((prev) => ({ ...prev, [tabKey]: false }));
    }
  };

  // Fetch data when tab changes (lazy-load each tab once)
  useEffect(() => {
    if (!initialized[tab]) {
      fetchTab(tab);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentAlerts = data[tab] || [];

  const removeAlertLocal = (id, tabKey) => {
    setData((prev) => ({
      ...prev,
      [tabKey]: prev[tabKey].filter((a) => a.id !== id),
    }));
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const computeHistoryStatus = (alert) => {
    if (alert.isActive) return "Active";
    // You can refine this once history DTO includes fulfilledAt, etc.
    return "Inactive";
  };

  const renderTimeSection = (alert) => {
    if (tab === "triggered") {
      return (
        <p className="text-sm text-gray-500 mt-1">
          Fulfilled At:{" "}
          <span className="font-medium">
            {formatDateTime(alert.fulfilledAt)}
          </span>
        </p>
      );
    }

    if (tab === "active") {
      return (
        <>
          <p className="text-sm text-gray-500 mt-1">
            Alert Window:{" "}
            <span className="font-medium">
              {formatDateTime(alert.startDateTime)} –{" "}
              {formatDateTime(alert.endDateTime)}
            </span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Created: {formatDateTime(alert.createdAt)}
          </p>
        </>
      );
    }

    // history
    return (
      <>
        <p className="text-sm text-gray-500 mt-1">
          Alert Window:{" "}
          <span className="font-medium">
            {formatDateTime(alert.startDateTime)} –{" "}
            {formatDateTime(alert.endDateTime)}
          </span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Created: {formatDateTime(alert.createdAt)}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Status:{" "}
          <span className="font-semibold text-gray-800">
            {computeHistoryStatus(alert)}
          </span>
        </p>
      </>
    );
  };

  const renderTitle = (alert) => {
    if (tab === "triggered") {
      return alert.providerName;
    }
    // active/history — alerts are generic, no specific provider yet
    const loc =
      [alert.city, alert.state].filter(Boolean).join(", ") || "Location N/A";
    return `${alert.specialty || "Any specialty"} – ${loc}`;
  };

  const renderSubtitle = (alert) => {
    if (tab === "triggered") {
      const hasFacilities = alert.facilities && alert.facilities.length > 0;
      return (
        <>
          <p className="text-sm text-gray-600">{alert.specialty}</p>

          {/* Facility Locations */}
          <div className="mt-2">
            <p className="text-sm font-semibold text-gray-900">
              Facility Locations:
            </p>
            <ul className="mt-1 space-y-1">
              {hasFacilities
                ? alert.facilities.map((f, idx) => (
                    <li
                      key={idx}
                      className="text-sm text-gray-700 leading-relaxed"
                    >
                      {f.name && (
                        <span className="mr-1 text-gray-900 font-medium">
                          ▸ {f.name}
                        </span>
                      )}
                      {!f.name && (
                        <span className="mr-1 text-gray-900 font-medium">
                          ▸
                        </span>
                      )}
                      <span className="text-gray-700">
                        {f.addressLine
                          ? ` — ${f.addressLine}, ${f.city}, ${f.state}`
                          : ` — ${[f.city, f.state].filter(Boolean).join(", ")}`}
                      </span>

                      <button
                        type="button"
                        aria-label={`Open directions to ${f.addressLine || ""}, ${f.city}, ${f.state}`}
                        className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:underline"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(
                            mapsLink(f),
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      >
                        <Navigation className="w-4 h-4 text-blue-600" />
                        <span className="text-sm">Directions</span>
                      </button>
                    </li>
                  ))
                : // Fallback to basic city/state from alert data
                  (alert.locations || []).map((loc, idx) => {
                    const locStr = [loc.city, loc.state]
                      .filter(Boolean)
                      .join(", ");
                    return locStr ? (
                      <li key={idx} className="text-sm text-gray-700">
                        ▸ {locStr}
                      </li>
                    ) : null;
                  })}
            </ul>
          </div>

          {alert.providerEmail && (
            <p className="text-sm text-blue-700 mt-2">{alert.providerEmail}</p>
          )}
          {alert.phoneNumber && (
            <p className="text-sm text-gray-600">☎ {alert.phoneNumber}</p>
          )}
        </>
      );
    }
    return (
      <p className="text-sm text-gray-600">
        Specialty: {alert.specialty || "N/A"}
      </p>
    );
  };

  return (
    <div>
      <h3 className="text-xl font-semibold mb-4">Provider Alerts</h3>

      {/* Tabs */}
      <div className="flex space-x-3 mb-4">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded ${
              tab === key
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {loading[tab] && (
        <p className="text-gray-500">
          Loading {TABS.find((t) => t.key === tab)?.label}…
        </p>
      )}

      {/* Error state */}
      {!loading[tab] && error[tab] && (
        <div className="mb-4 p-3 rounded border border-red-300 bg-red-50 text-sm text-red-700">
          <p>{error[tab]}</p>
          <button
            onClick={() => fetchTab(tab)}
            className="mt-2 inline-flex items-center px-3 py-1 rounded bg-red-600 text-white text-xs hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* List / empty state */}
      {!loading[tab] && !error[tab] && (
        <>
          {currentAlerts.length === 0 ? (
            <p className="text-gray-500">
              No {TABS.find((t) => t.key === tab)?.label.toLowerCase()}.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {currentAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm${
                    tab === "triggered"
                      ? " cursor-pointer hover:shadow-md transition"
                      : ""
                  }`}
                  onClick={
                    tab === "triggered"
                      ? () => {
                          if (!alert.providerId || !patientId) return;
                          navigate(
                            `/provider/${alert.providerId}/patient/${patientId}`,
                          );
                        }
                      : undefined
                  }
                >
                  <h4 className="text-lg font-bold text-gray-800">
                    {renderTitle(alert)}
                  </h4>
                  {renderSubtitle(alert)}
                  {renderTimeSection(alert)}

                  <div className="mt-4 flex flex-wrap gap-3">
                    {tab === "triggered" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!alert.providerId || !patientId) return;
                          navigate(
                            `/provider/${alert.providerId}/patient/${patientId}`,
                          );
                        }}
                        className="bg-blue-600 text-white text-sm px-4 py-1 rounded hover:bg-blue-700"
                      >
                        Book Appointment
                      </button>
                    )}

                    {tab !== "history" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAlertLocal(alert.id, tab);
                        }}
                        className="text-sm text-red-600 hover:underline"
                      >
                        🗑 Remove Alert
                      </button>
                    )}

                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm text-green-600 hover:underline"
                    >
                      📧 Contact
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
